import { useNavigate, useParams } from "react-router";
import { Alert, Button, button, Link } from "@heroui/react";
import { useShallow } from "zustand/shallow";
import { CopyIcon, FileXIcon, InfoIcon, NoteIcon, SignInIcon } from "@phosphor-icons/react";

import { api } from "@/services/api";
import { PageDataPlaceholder } from "@/shared/components/ui/PageDataPlaceholder";
import { SharedNoteEditor } from "@/features/notes/components/SharedNoteEditor";
import { useBoundStore } from "@/app/store";
import { ICON_SIZE, ROUTES_TO } from "@/config/constants";
import { useCallback, useMemo, useState } from "react";
import { ManageTheme } from "@/routes/private/settings/components/ManageTheme";
import { usePageLayout } from "@/app/hooks/usePageLayout";

export function ShareNoteRoute() {
  const { noteUuid } = useParams();
  const [isCopying, setIsCopying] = useState(false);
  const { data: note, isLoading: isLoadingNote } = api.hooks.notes.useNoteByPublicUuid(noteUuid);
  const { isAuthenticated, userId, addNote } = useBoundStore(
    useShallow((state) => ({
      isAuthenticated: state.user.isAuthenticated,
      userId: state.user.id,
      addNote: state.notes.addNote,
    }))
  );
  const deviceType = useBoundStore(useShallow((state) => state.device.type));
  const navigate = useNavigate();

  const handleCopyNote = useCallback(async () => {
    if (!note || !userId) return;
    const { html_content, text_content } = note;
    setIsCopying(true);
    addNote({
      html_content,
      text_content,
      user_id: userId,
      folder_id: null,
    }).then((note) => {
      if (note) {
        if (deviceType === "mobile") {
          navigate("/notes/0", { replace: true });
          navigate(`/notes/0/${note.id}`);
        } else {
          navigate(`/notes?sharedNoteId=${note.id}`);
        }
      }
    });
  }, [note, userId, addNote, deviceType, navigate]);

  const pageLayoutConfig = useMemo(
    () => ({
      className: "pb-24",
      navbar: {
        actions: isAuthenticated
          ? [
              <Button color="primary" isLoading={isCopying} key="copy" size="sm" onPress={handleCopyNote}>
                {!isCopying && <CopyIcon size={ICON_SIZE.sm} />} Copy note
              </Button>,
              <ManageTheme />,
            ]
          : [
              <Link
                className={button({ size: "sm", variant: "light" })}
                key="about-us"
                href="https://www.inkrows.com"
                isExternal
              >
                <InfoIcon size={ICON_SIZE.sm} />
                About us
              </Link>,
              <Link className={button({ color: "primary", size: "sm" })} key="sign-in" href="/sign-in">
                <SignInIcon size={ICON_SIZE.sm} /> Sign in
              </Link>,
              <ManageTheme />,
            ],
        hideBack: !isAuthenticated,
        hideLogo: false,
        title: "InkRows",
        onBack: () => navigate(ROUTES_TO.home, { replace: true }),
      },
    }),
    [isAuthenticated, isCopying, handleCopyNote, navigate]
  );

  usePageLayout(pageLayoutConfig);

  if (isLoadingNote) {
    return <PageDataPlaceholder icon={<NoteIcon />} title="Loading note..." />;
  }

  if (!note) {
    return (
      <PageDataPlaceholder
        description="This note has been deleted or is no longer shared. Only notes with active sharing links can be accessed."
        icon={<FileXIcon />}
        title="Note not found"
      />
    );
  }

  return (
    <>
      <SharedNoteEditor
        data={{
          html_content: note.html_content,
          text_content: note.text_content,
        }}
        refetchedNote={note ?? null}
      />
      {!isAuthenticated && (
        <Alert
          className="bg-content2 fixed bottom-2 left-1/2 w-[calc(100%-1rem)] max-w-2xl -translate-x-1/2 items-center"
          description="Sign in to copy this note to your account and edit it."
          endContent={
            <Link className={button({ color: "primary", size: "sm", isIconOnly: true })} key="sign-in" href="/sign-in">
              <SignInIcon size={ICON_SIZE.sm} />
            </Link>
          }
          variant="solid"
        />
      )}
    </>
  );
}
